import { defineStore } from "pinia";
import { CWebsocket } from "@/assets/js/websocketHelper";
import type { TLobbyData, TWsData } from "@/assets/js/globalTypes";
import { useDefaultStore } from "@/store";

import type { Router } from "vue-router";
import { resetGameState } from "@/assets/js/helpers";

const router = shallowRef<Router | null>(null);

function setRouterInstance(routerInstance: Router) {
	router.value = routerInstance;
}

type TMultiplayerMovie = {
	artist?: string | null;
	artist_credit?: string | null;
	backdrop_path: string | null;
	birth_date?: string | null;
	cover_thumb_250_url?: string | null;
	cover_thumb_500_url?: string | null;
	cover_url?: string | null;
	id: number;
	image_url?: string | null;
	media_type?: "movie" | "music" | "person";
	name?: string | null;
	original_title?: string | null;
	photo_url?: string | null;
	poster_path: string | null;
	profile_image_url?: string | null;
	profile_path?: string | null;
	title: string;
};

type TMultiplayerPlayer = {
	is_me: boolean;
	is_on_turn?: boolean;
	mpg_usr_color_hex: string | null;
	mpg_usr_has_left_game?: 1 | 0;
	mpg_usr_is_eliminated?: 1 | 0;
	mpg_usr_is_creator: 1 | 0;
	mpg_usr_order?: number;
	usr_username: string | null;
};

type TMultiplayerResultPlayer = TMultiplayerPlayer & {
	is_winner: boolean;
	mpg_usr_has_left_game: 1 | 0;
	mpg_usr_is_eliminated: 1 | 0;
	mpg_usr_order: number;
	rank: number;
	reached_round: number;
};

export const useWebSocketStore = defineStore("web-socket", () => {
	const wsText = ref("");
	const wsInstance = ref<CWebsocket | null>(null);

	const MESSAGE_TYPES = {
		PLAYER_JOIN_ROOM: "PJRM",
		PLAYER_JOIN_GAME: "PJGM",
		SHOW_JOIN_DIALOG: "JNDG",
		REDIRECT_TO_LOBBY: "LOBB",
		GET_READY: "RDY_",
		MPG_PLAYER_ANSWER: "MGPA",
		MPG_PLAYER_LEAVE: "MGPL",
		MPG_PLAY_AGAIN: "MPGA",
		ERROR: "ERR_",
		CREATE_ROOM: "CRRM",
		WRONG_PASSWORD: "WRPW",
		START_GAME: "STGM",
		GAME_STARTED: "GSTR",
		MULTIPLAYER_GAME_OVER: "MGPO",
	} as const;

	function resolveCategory(
		category?: string,
		fallback = useDefaultStore().multiplayerGameCategory,
	) {
		if (category === useDefaultStore().MAIN_CATEGORIES.MOVIES) {
			return useDefaultStore().MAIN_CATEGORIES.MOVIES;
		}

		if (category === useDefaultStore().MAIN_CATEGORIES.MUSIC) {
			return useDefaultStore().MAIN_CATEGORIES.MUSIC;
		}

		if (category === useDefaultStore().MAIN_CATEGORIES.PEOPLE) {
			return useDefaultStore().MAIN_CATEGORIES.PEOPLE;
		}

		return fallback;
	}

	function getCategoryHomePath(category = useDefaultStore().multiplayerGameCategory) {
		if (category === useDefaultStore().MAIN_CATEGORIES.MUSIC) {
			return "/music";
		}

		if (category === useDefaultStore().MAIN_CATEGORIES.PEOPLE) {
			return "/people";
		}

		return "/movies";
	}

	function getWeakestLinkRouteName(category = useDefaultStore().multiplayerGameCategory) {
		if (category === useDefaultStore().MAIN_CATEGORIES.MUSIC) {
			return "MusicWeakestLinkPage";
		}

		if (category === useDefaultStore().MAIN_CATEGORIES.PEOPLE) {
			return "PeopleWeakestLinkPage";
		}

		return "MoviesWeakestLinkPage";
	}

	const MESSAGE_HANDLER = {
		[MESSAGE_TYPES.ERROR]: (data: {
			message: string;
			is_game_over: boolean;
			mpg_token?: string;
		}) => {
			if (data.mpg_token && !isValidRoom(data.mpg_token)) return;

			const fallbackPath = getCategoryHomePath();

			resetGameState();
			router.value?.push(fallbackPath);
			useDefaultStore().errorMessage = data?.message;
			useDefaultStore().errorToastTrigger = true;
		},

		[MESSAGE_TYPES.SHOW_JOIN_DIALOG]: (data: {
			is_protected: boolean;
			mpg_token: string;
		}) => {
			if (!isValidRoom(data.mpg_token)) return;

			resetGameState();
			useDefaultStore().isRoomProtected = data.is_protected;
			useDefaultStore().isVisibleJoinDialog = true;
		},

		[MESSAGE_TYPES.REDIRECT_TO_LOBBY]: (data: TLobbyData) => {
			if (!isValidRoom(data.mpg_token)) return;

			resetGameState();
			useDefaultStore().multiplayerGameCategory = resolveCategory(data.category);
			useDefaultStore().gamePlayers = data?.players;
			useDefaultStore().isVisibleLobbyModal = true;
		},

		[MESSAGE_TYPES.GET_READY]: (data: { mpg_token: string }) => {
			if (!isValidRoom(data.mpg_token)) return;

			useDefaultStore().getReadyState = true;
		},

		[MESSAGE_TYPES.WRONG_PASSWORD]: (data: { mpg_token: string }) => {
			if (!isValidRoom(data.mpg_token)) return;

			useDefaultStore().isWrongPassword = true;
		},

		[MESSAGE_TYPES.GAME_STARTED]: (data: {
			albums?: TMultiplayerMovie[];
			category?: string;
			isActive: boolean;
			items?: TMultiplayerMovie[];
			movies?: TMultiplayerMovie[];
			mpg_token: string;
			people?: TMultiplayerMovie[];
			players: TMultiplayerPlayer[];
			secondsLeft: number;
			round?: number;
			isCorrect?: boolean;
		}) => {
			if (!isValidRoom(data.mpg_token)) return;

			resetGameState();
			useDefaultStore().multiplayerGameCategory = resolveCategory(data.category);
			useDefaultStore().multiplayerGamePlayerList = data.players;
			useDefaultStore().multiplayerGameMovies =
				data.items || data.people || data.albums || data.movies || [];
			useDefaultStore().multiplayerGameSecondsLeft = data.secondsLeft;
			useDefaultStore().multiplayerGameIsActive = data.isActive;
			useDefaultStore().multiplayerGameIsCorrect = data.isCorrect ?? null;
			useDefaultStore().multiplayerGameRound =
				data.round ?? useDefaultStore().multiplayerGameRound;
			useDefaultStore().multiplayerGameActive = true;
		},

		[MESSAGE_TYPES.MULTIPLAYER_GAME_OVER]: (data: {
			mpg_token: string;
			players: TMultiplayerResultPlayer[];
		}) => {
			if (!isValidRoom(data.mpg_token)) return;

			const category = useDefaultStore().multiplayerGameCategory;
			resetGameState();
			useDefaultStore().multiplayerGameCategory = category;
			useDefaultStore().gameToken = data.mpg_token;
			useDefaultStore().multiplayerGameResults = data.players;
			useDefaultStore().multiplayerGameIsOver = true;
		},

		[MESSAGE_TYPES.MPG_PLAY_AGAIN]: async (data: {
			old_mpg_token: string;
			mpg_token: string;
			category: string;
			is_protected: boolean;
		}) => {
			if (!isValidRoom(data.old_mpg_token)) return;

			const category = resolveCategory(data.category);

			resetGameState();
			useDefaultStore().multiplayerGameCategory = category;
			useDefaultStore().gameToken = data.mpg_token;
			useDefaultStore().isRoomProtected = data.is_protected;
			useDefaultStore().isVisibleJoinDialog = true;

			await router.value?.replace({
				name: getWeakestLinkRouteName(category),
				params: { id: data.mpg_token },
			});
		},

		[MESSAGE_TYPES.CREATE_ROOM]: async (data: {
			category?: string;
			plr_token: string;
			mpg_token: string;
		}) => {
			const category = data.category
				? resolveCategory(data.category)
				: useDefaultStore().multiplayerGameCategory;

			resetGameState();
			useDefaultStore().multiplayerGameCategory = category;

			await router.value?.push({
				name: getWeakestLinkRouteName(category),
				params: { id: data.mpg_token },
			});
		},
	};

	const handleMessageType = function (type: keyof typeof MESSAGE_HANDLER) {
		return MESSAGE_HANDLER[type] || null;
	};

	const waitUntilOpen = function (timeoutMs = 10_000) {
		if (wsInstance.value?.wsStatus === "Open") {
			return Promise.resolve();
		}

		return new Promise<void>((resolve, reject) => {
			const startedAt = Date.now();
			const intervalId = window.setInterval(() => {
				if (wsInstance.value?.wsStatus === "Open") {
					window.clearInterval(intervalId);
					resolve();
					return;
				}

				if (Date.now() - startedAt >= timeoutMs) {
					window.clearInterval(intervalId);
					reject(new Error("WebSocket connection timed out."));
				}
			}, 50);
		});
	};

	const safeSend = async function (msg: any) {
		try {
			await waitUntilOpen();
			wsInstance.value?.send(msg);
			return true;
		} catch (err: any) {
			console.warn(err.message);
			return false;
		}
	};

	const wsRoot = "/api_v1/websockets/main";

	const wsURL = computed(() => {
		const thisLocation = globalThis.location.host;
		const thisProtocol = globalThis.location.protocol;

		const protocol = thisProtocol.startsWith("https") ? "wss" : "ws";
		if (thisLocation.startsWith("localhost")) {
			return import.meta.env.VITE_APP_ROOT_API + "/websockets/main";
		}
		return `${protocol}://${thisLocation}${wsRoot}`;
	});

	function initWs(plr_token: string) {
		const wsUrl =
			wsURL.value +
			(plr_token && plr_token !== "null" ? `?plr_token=${plr_token}` : "");

		const options: ConstructorParameters<typeof CWebsocket>[1] = {
			cb: {
				onOpen(ws, evt) {
					useDefaultStore().isWsConnecting = false;
				},
				onMessageData: async (ws, evt, data) => {
					wsText.value = ws.logs.join("\n");

					const type = (data as unknown as TWsData).type;
					const responder = handleMessageType(
						type as keyof typeof MESSAGE_HANDLER,
					);

					if (!responder) return;

					const msgData = (data as unknown as TWsData).data;
					responder(msgData as never);
				},
				onError(ws, evt) {
					useDefaultStore().isWsConnecting = true;
					console.error("WebSocket Error", evt);
					wsText.value = ws.logs.join("\n");
				},
				onClose(ws, evt) {
					useDefaultStore().isWsConnecting = false;
					wsText.value = ws.logs.join("\n");
				},
			},
			useLogs: false,
			autoReconnect: true,
			retries: 5,
			delayReconnect: 2000,
			pingInterval: 10_000,
			getPingPayload: () => ({
				plr_token: useDefaultStore().playerToken ?? "",
				gms_token: useDefaultStore().mmgToken ?? "",
			}),
		};
		wsInstance.value = new CWebsocket(wsUrl, options);
		wsText.value = wsInstance.value.logs.join("\n");
	}

	function getCurrentRoomId(): string | null {
		const params = router.value?.currentRoute.value.params as
			| Record<string, unknown>
			| undefined;
		const rawId = params?.id;

		if (Array.isArray(rawId)) {
			return typeof rawId[0] === "string" ? rawId[0] : null;
		}

		return typeof rawId === "string" ? rawId : null;
	}

	function isValidRoom(gmsToken: string): boolean {
		return getCurrentRoomId() === gmsToken;
	}

	return {
		initWs,
		wsInstance,
		waitUntilOpen,
		safeSend,
		MESSAGE_TYPES,
		setRouterInstance,
	};
});
